/**
 * Trade-date and par presets shared by the Markets History filter bar and the
 * Bond Details Trades tab. Both send them to `GET /api/v1/tradehistory/search`
 * as `TradeDate.Min` / `TradeDate.Max` and `ParTraded.Min`.
 */

/** Rolling trade-date window ending today. `all` drops the date bound. */
export type TradeDateRangePreset = "1d" | "7d" | "30d" | "90d" | "1y" | "all";

export const TRADE_DATE_RANGE_OPTIONS: readonly { id: TradeDateRangePreset; label: string }[] = [
  { id: "7d", label: "7D" },
  { id: "30d", label: "30D" },
  { id: "90d", label: "90D" },
  { id: "1y", label: "1Y" },
  { id: "all", label: "All" },
];

/**
 * Drawer Trades-tab date presets. Adds a 1D (today-only) preset in front of the
 * shared Markets options; the Markets History bar keeps its own "Today" entry
 * and does not surface 1D.
 */
export const BOND_TRADES_DATE_RANGE_OPTIONS: readonly {
  id: TradeDateRangePreset;
  label: string;
}[] = [{ id: "1d", label: "1D" }, ...TRADE_DATE_RANGE_OPTIONS];

/** Opening window of the Bond Details Trades tab. */
export const DEFAULT_BOND_TRADES_RANGE: TradeDateRangePreset = "90d";

/**
 * Minimum-par preset. Muni `M` is $1,000; `5MM+` is the MSRB $5 million mask
 * threshold.
 */
export type TradeSizePreset = "100m" | "500m" | "1000m" | "5mm";

/** A size preset or `all` (no par bound), as shown in the size dropdowns. */
export type TradeSizeSelection = TradeSizePreset | "all";

export const TRADE_SIZE_OPTIONS: readonly {
  id: TradeSizeSelection;
  label: string;
  /** Inclusive `ParTraded.Min` in raw dollars. Absent for All sizes. */
  minPar?: number;
}[] = [
  { id: "all", label: "All sizes" },
  { id: "100m", label: "≥ 100M", minPar: 100_000 },
  { id: "500m", label: "≥ 500M", minPar: 500_000 },
  { id: "1000m", label: "≥ 1000M", minPar: 1_000_000 },
  { id: "5mm", label: "5MM+", minPar: 5_000_000 },
];

export const DEFAULT_BOND_TRADES_SIZE: TradeSizeSelection = "all";

const DATE_RANGE_IDS = new Set<string>(TRADE_DATE_RANGE_OPTIONS.map((option) => option.id));
const SIZE_PRESET_IDS = new Set<string>(
  TRADE_SIZE_OPTIONS.filter((option) => option.id !== "all").map((option) => option.id),
);

export function isTradeDateRangePreset(value: string): value is TradeDateRangePreset {
  return DATE_RANGE_IDS.has(value);
}

export function isTradeSizePreset(value: string): value is TradeSizePreset {
  return SIZE_PRESET_IDS.has(value);
}
